import { FC, useContext, useState } from 'react';

import {
  Button,
  H3,
  Paragraph,
  PasswordInput,
  Separator,
  Tooltip,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import s from './SmartLockPassword.module.scss';

export const SmartLockPassword: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    actions: { setSetting },
  } = useContext(SettingsContext);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: { password: '' },
    mode: 'onChange',
  });

  const [isEditMode, setIsEditMode] = useState(false);

  const passwordData = watch('password');

  return (
    <div className={s.container}>
      <div className={s.header}>
        <H3>{t('setting.smartLock.password')}</H3>
        <Tooltip
          contentClassName={s.tooltip_content}
          content={
            <div className={s.tooltip}>
              <H3>{t('setting.smartLock.password')}</H3>
              <span className={s.tooltip_subtitle}>
                {t('settings.smartLock.security.tooltip.description')}
              </span>
            </div>
          }
          position="bottom-right"
        />
      </div>
      <span className={s.subtitle}>
        {t('setting.smartLock.password.description')}
      </span>
      <Separator style={{ margin: '16px 0' }} />
      <form
        className={s['password-container']}
        onSubmit={handleSubmit(({ password }) => {
          if (!password) {
            return setIsEditMode(false);
          }

          const normalizePassword = password
            .split('')
            .map((char) => {
              switch (char) {
                case '▲':
                  return 'U';
                case '▶':
                  return 'R';
                case '▼':
                  return 'D';
                case '◄':
                  return 'L';
              }
            })
            .join('');

          return setSetting({
            alias: ESettingsAliases.FASTOFF_SMART_LOCK_PWD,
            value: normalizePassword,
          }).then(() => {
            reset({ password: '' });
          });
        })}
      >
        <div className={s.password}>
          <Controller
            control={control}
            name="password"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <PasswordInput
                variant="secondary"
                value={value}
                onChange={(val) => {
                  onChange(val.replace(/[^◄▼▲▶]/g, ''));
                }}
                disabled={isSubmitting || !isEditMode}
                autoComplete="off"
                error={error?.message}
                bordered
                placeholder={'•••••'}
              />
            )}
          />
        </div>
        <Button
          type={isEditMode ? 'submit' : 'button'}
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();

            if (!isEditMode) {
              setIsEditMode(true);
              e.preventDefault();
            }
          }}
          disabled={isSubmitting || (isEditMode && passwordData.length !== 5)}
          iconName={isEditMode ? 'CheckIcon' : 'EditIcon'}
          className={s.btn}
          onlyIcon
        />
      </form>
      <Paragraph color="secondary">
        {`${t('length')}: ${passwordData.length}/5`}
      </Paragraph>
      <div className={s.buttons}>
        <Button
          variant="outline"
          onClick={() => {
            if (passwordData.length < 5) {
              setValue('password', passwordData + '▲');
            }
          }}
          disabled={passwordData.length === 5 || !isEditMode}
        >
          {t('up')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            if (passwordData.length < 5) {
              setValue('password', passwordData + '▶');
            }
          }}
          disabled={passwordData.length === 5 || !isEditMode}
        >
          {t('right')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            if (passwordData.length < 5) {
              setValue('password', passwordData + '▼');
            }
          }}
          disabled={passwordData.length === 5 || !isEditMode}
        >
          {t('down')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            if (passwordData.length < 5) {
              setValue('password', passwordData + '◄');
            }
          }}
          disabled={passwordData.length === 5 || !isEditMode}
        >
          {t('left')}
        </Button>
      </div>
    </div>
  );
};
